import { useState } from "react";
import { ChevronRight, Percent, Sparkles, Tag, ArrowRight, ShoppingBag } from "lucide-react";
import { Link } from "react-router-dom";

interface SubCategory {
  id: string;
  name: string;
  slug: string;
  children?: SubCategory[];
}

interface FeaturedProduct {
  id: string;
  name: string;
  artist?: string;
  category?: string;
  price: number;
  originalPrice?: number;
  image: string;
  badge?: string;
}

interface TopBanner {
  id: string;
  title: string;
  image: string;
  link: string;
}

interface MegaMenuProps {
  isOpen: boolean;
  onClose: () => void;
  categories: SubCategory[];
  featuredProducts: FeaturedProduct[];
  topBanners: TopBanner[];
}

const mainCategories = [
  { id: "muzi", name: "Muži", slug: "muzi", image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop" },
  { id: "zeny", name: "Ženy", slug: "zeny", image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop" },
  { id: "deti", name: "Deti", slug: "deti", image: "https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?w=100&h=100&fit=crop" },
  { id: "doplnky", name: "Doplnky", slug: "doplnky", image: "https://images.unsplash.com/photo-1523170335258-f5ed11844a49?w=100&h=100&fit=crop" },
  { id: "hudba", name: "Hudba", slug: "hudba", image: "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=100&h=100&fit=crop" },
];

// Subcategories per main category - can have different number of groups
const categorySubItems: Record<string, { group: string; items: string[] }[]> = {
  muzi: [
    { group: "Oblečenie", items: ["Tričká", "Tielka", "Mikiny", "Bundy", "Polokošele", "Vesty"] },
    { group: "Spodná časť", items: ["Nohavice", "Kraťasy", "Ponožky", "Spodné prádlo", "Legíny"] },
    { group: "Doplnky", items: ["Obuv", "Čiapky", "Šiltovky", "Šatky", "Opasky", "Tašky"] },
    { group: "Špeciálne", items: ["Limitky", "Výpredaj", "Novinky", "Bestsellery"] },
  ],
  zeny: [
    { group: "Oblečenie", items: ["Tričká", "Tielka", "Mikiny", "Šaty", "Sukne"] },
    { group: "Spodná časť", items: ["Nohavice", "Legíny", "Kraťasy"] },
    { group: "Doplnky", items: ["Kabelky", "Šatky", "Šperky", "Obuv"] },
  ],
  deti: [
    { group: "Oblečenie", items: ["Tričká", "Mikiny", "Bundy"] },
    { group: "Veľkosti", items: ["86-110", "122-140", "152-170"] },
  ],
  doplnky: [
    { group: "Doplnky", items: ["Čiapky", "Šiltovky", "Tašky", "Peňaženky", "Opasky", "Šatky"] },
    { group: "Elektronika", items: ["Slúchadlá", "Príslušenstvo"] },
    { group: "Domácnosť", items: ["Hrnčeky", "Plagáty", "Podložky"] },
  ],
  hudba: [
    { group: "Formáty", items: ["CD", "Vinyl", "Kazety", "Merch balíky"] },
    { group: "Žánre", items: ["Hip-Hop", "Rock", "Pop", "Elektronika"] },
  ],
};

const featuredCategories = [
  { icon: Percent, label: "Zvýhodnené balíky", description: "Ušetri až 30%", color: "bg-amber-500/10 text-amber-600 hover:bg-amber-500/20", slug: "baliky" },
  { icon: Tag, label: "Výpredaj", description: "Až -70% zľava", color: "bg-destructive/10 text-destructive hover:bg-destructive/20", slug: "zlava" },
  { icon: Sparkles, label: "Limitované edície", description: "Exkluzívne kusy", color: "bg-purple-500/10 text-purple-600 hover:bg-purple-500/20", slug: "limitovane" },
];

// Demo featured product with more details
const demoFeaturedProduct: FeaturedProduct = {
  id: "featured-1",
  name: "Premium Hoodie Collection",
  artist: "Separ",
  category: "Mikiny",
  price: 29.99,
  originalPrice: 39.99,
  image: "https://images.unsplash.com/photo-1618354691373-d851c5c3a990?w=200&h=200&fit=crop",
  badge: "HOT",
};

export const MegaMenu = ({
  isOpen,
  onClose,
  featuredProducts,
}: MegaMenuProps) => {
  const [activeCategory, setActiveCategory] = useState(mainCategories[0].id);

  if (!isOpen) return null;

  const currentSubCategories = categorySubItems[activeCategory] || [];
  const product = featuredProducts[0] || demoFeaturedProduct;
  
  // Calculate grid columns based on number of subcategory groups (min 2, max 4)
  const colCount = Math.min(Math.max(currentSubCategories.length, 2), 4);
  const gridColsClass = {
    2: "grid-cols-2",
    3: "grid-cols-3",
    4: "grid-cols-4",
  }[colCount] || "grid-cols-4";

  return (
    <div className="absolute left-0 right-0 top-full z-[110] bg-background border-b border-border shadow-2xl animate-slide-down" style={{ animationDuration: '200ms' }}>
      <div className="max-w-[1600px] mx-auto px-4 lg:px-6 py-8">
          <div className="grid grid-cols-12 gap-8">
            {/* Left - Main Categories as visual cards */}
            <div className="col-span-2 space-y-2">
              <h4 className="text-xs font-bold text-primary uppercase tracking-widest mb-4">
                Nakupovať pre
              </h4>
              {mainCategories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/kategoria/${cat.slug}`}
                  onClick={onClose}
                  onMouseEnter={() => setActiveCategory(cat.id)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all group ${
                    activeCategory === cat.id
                      ? "bg-primary/10 ring-1 ring-primary/20"
                      : "hover:bg-secondary"
                  }`}
                >
                  <img 
                    src={cat.image} 
                    alt={cat.name}
                    className="w-10 h-10 rounded-lg object-cover"
                  />
                  <span className={`text-sm font-semibold transition-colors ${
                    activeCategory === cat.id ? "text-primary" : "text-foreground group-hover:text-primary"
                  }`}>
                    {cat.name}
                  </span>
                  <ChevronRight className={`w-4 h-4 ml-auto transition-all ${
                    activeCategory === cat.id ? "text-primary translate-x-0.5" : "text-muted-foreground"
                  }`} />
                </Link>
              ))}
            </div>

            {/* Middle - Subcategories in organized groups - dynamic columns */}
            <div className={`col-span-6 grid ${gridColsClass} gap-6`}>
              {currentSubCategories.map((group, groupIndex) => (
                <div 
                  key={`${activeCategory}-${groupIndex}`} 
                  className="animate-slide-up"
                  style={{ animationDelay: `${groupIndex * 50}ms`, animationFillMode: 'backwards' }}
                >
                  <h4 className="text-xs font-bold text-primary uppercase tracking-widest mb-4 pb-2 border-b border-primary/20">
                    {group.group}
                  </h4>
                  <div className="space-y-0.5">
                    {group.items.map((item, itemIndex) => (
                      <Link
                        key={item}
                        to={`/kategoria/${item.toLowerCase().replace(/\s/g, "-")}`}
                        onClick={onClose}
                        className="group flex items-center gap-3 py-2 px-2.5 -mx-2.5 rounded-lg text-sm text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200"
                        style={{ animationDelay: `${(groupIndex * 50) + (itemIndex * 20)}ms` }}
                      >
                        <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/50 group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
                        <span className="font-medium">{item}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
              
              {/* Fill empty space if less than 4 columns - show "View All" cards */}
              {currentSubCategories.length < 4 && (
                <div className={`${currentSubCategories.length === 2 ? 'col-span-2' : ''}`}>
                  <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-4 pb-2 border-b border-border">
                    Rýchle odkazy
                  </h4>
                  <div className="space-y-2">
                    <Link
                      to={`/kategoria/${activeCategory}`}
                      onClick={onClose}
                      className="flex items-center gap-3 p-3 rounded-xl bg-secondary/50 hover:bg-secondary transition-colors group"
                    >
                      <ShoppingBag className="w-5 h-5 text-primary" />
                      <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                        Zobraziť všetko
                      </span>
                      <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary ml-auto transition-colors" />
                    </Link>
                    <Link
                      to="/novinky"
                      onClick={onClose}
                      className="flex items-center gap-3 p-3 rounded-xl bg-secondary/50 hover:bg-secondary transition-colors group"
                    >
                      <Sparkles className="w-5 h-5 text-amber-500" />
                      <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                        Novinky
                      </span>
                      <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary ml-auto transition-colors" />
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Right - Featured Product + Categories */}
            <div className="col-span-4 space-y-4">
              {/* Featured Product - Enhanced without border */}
              <Link
                to={`/produkt/${product.id}`}
                onClick={onClose}
                className="block group relative overflow-hidden rounded-2xl bg-gradient-to-br from-secondary via-secondary/80 to-primary/10 hover:to-primary/20 transition-all"
              >
                <div className="flex gap-4 p-4">
                  <div className="relative w-32 h-32 rounded-xl overflow-hidden bg-secondary flex-shrink-0 shadow-xl">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    {product.badge && (
                      <div className="absolute top-2 left-2 px-2.5 py-1 rounded-lg bg-primary text-primary-foreground text-xs font-bold shadow-lg">
                        {product.badge}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0 py-1 flex flex-col">
                    <p className="text-[10px] font-bold text-primary uppercase tracking-widest mb-1">Odporúčame</p>
                    {product.category && (
                      <p className="text-xs text-muted-foreground mb-1">{product.category}</p>
                    )}
                    <p className="text-base font-bold text-foreground group-hover:text-primary transition-colors line-clamp-2 mb-1">
                      {product.name}
                    </p>
                    {product.artist && (
                      <p className="text-xs text-muted-foreground mb-auto">{product.artist}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xl font-black text-primary">
                        {product.price.toFixed(2).replace('.', ',')} €
                      </span>
                      {product.originalPrice && (
                        <span className="text-sm text-muted-foreground line-through">
                          {product.originalPrice.toFixed(2).replace('.', ',')} €
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-primary flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all shadow-lg">
                  <ArrowRight className="w-5 h-5 text-primary-foreground" />
                </div>
              </Link>

              {/* Featured Categories */}
              <div className="grid grid-cols-1 gap-2">
                {featuredCategories.map((item, index) => (
                  <Link
                    key={index}
                    to={`/kategoria/${item.slug}`}
                    onClick={onClose}
                    className={`flex items-center gap-4 p-4 rounded-xl transition-all group ${item.color}`}
                  >
                    <div className="w-10 h-10 rounded-lg bg-background/50 flex items-center justify-center">
                      <item.icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <span className="text-sm font-bold block">{item.label}</span>
                      <span className="text-xs opacity-70">{item.description}</span>
                    </div>
                    <ChevronRight className="w-4 h-4 opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                  </Link>
                ))}
              </div>
            </div>
        </div>
      </div>
    </div>
  );
};
